import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { copyFor, routes } from 'config';
import { Button, Text } from '@perch-labs/shrike';
import { today, parseDate, timeSince } from '@perch-labs/warbler';
import { useSessionContext } from 'contexts/session';
import { useProductStatus, ProductType } from 'hooks/use-product-status.hook';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import {
  useFunnelRedirect,
  RedirectReason,
} from 'hooks/use-funnel-redirect.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Card } from 'styles';
import { HighlightedCopyWrapper } from 'components/modules/ctas/ctas.style';
import {
  GET_DATA_FOR_BILL_PAY_CTA_QUERY,
  GET_BILLING_EXPERIENCE_OPTIONS,
} from './bill-pay-cta.api';

const getCopy = copyFor('cta.billPay');
const { paymentSettings } = routes();

export const BillPayCta = () => {
  const { userId } = useSessionContext();
  const { planId } = useParams<{ planId?: string }>();
  const history = useHistory();
  const billPay = useProductStatus(ProductType.BillPay, Number(planId));
  const flags = useFlags();

  const { data, loading, error } = useQuery<
    GetDataForBillPayCtaQuery,
    GetDataForBillPayCtaQueryVariables
  >(GET_DATA_FOR_BILL_PAY_CTA_QUERY, {
    skip: !planId,
    fetchPolicy: 'no-cache',
    variables: { planId: Number(planId) || -1 },
  });

  const enrollmentDataSourceName =
    data?.plan?.utilityAccounts[0]?.enrollmentDataSourceName;
  const utilityId = data?.plan?.utilityAccounts[0]?.utility?.id;

  const {
    data: optionsData,
    loading: optionsLoading,
    error: optionsError,
  } = useQuery<
    GetBillingExperienceOptionsQuery,
    GetBillingExperienceOptionsQueryVariables
  >(GET_BILLING_EXPERIENCE_OPTIONS, {
    skip: !enrollmentDataSourceName || !utilityId,
    variables: {
      enrollmentDataSourceName: enrollmentDataSourceName || 'none',
      utilityId: utilityId || -1,
    },
  });

  const [redirect, { loading: redirectLoading }] = useFunnelRedirect({
    idParams: { planId: Number(planId) },
    userId,
    reason: RedirectReason.BillPayAvailable,
  });

  if (loading || billPay.loading || optionsLoading) {
    return <LoadingModule variant="cta" />;
  }

  if (error || billPay.error || optionsError) {
    return <ErrorModule />;
  }

  const { utilityAccounts, paymentMethod } = data?.plan || {};
  const targetBillExperience = (utilityAccounts?.[0] as any)
    ?.targetBillExperience;
  const isDualBilled = targetBillExperience === 'dual_billed';
  const billingExperienceOptions = optionsData?.billingExperienceOptions || [];
  const hasBillingExperienceOptions = billingExperienceOptions.length > 0;
  const isACBEnrollable = billingExperienceOptions
    .map(o => o.name)
    .includes('acb');
  const hasLeadButNoPaymentMethod =
    !!billPay?.data?.isPending && !paymentMethod;

  // Feature flag: cs-acb-retirement
  // When enabled, only show payment method CTA for dual_billed accounts,
  // and show dual billing title/copy for those accounts
  const csAcbRetirement = flags.csAcbRetirement;
  const shouldCheckDualBilled = csAcbRetirement ? isDualBilled : true;

  const showComponent =
    shouldCheckDualBilled &&
    ((hasBillingExperienceOptions && isACBEnrollable && !paymentMethod) ||
      hasLeadButNoPaymentMethod);

  if (!showComponent) return null;

  const needsBillPay =
    enrollmentDataSourceName === 'utility_connect' &&
    !billPay?.data?.isAvailable;

  const { mostRecentUtilityStatement, utility } = utilityAccounts?.[0] || {};
  const eventDate = parseDate(mostRecentUtilityStatement?.dueDate);
  const todayDate = today();
  const daysUntilBillDue = timeSince(todayDate, { baseDate: eventDate });

  const highlightedText = needsBillPay
    ? getCopy('highlightedText', {
        numberOfDays: daysUntilBillDue,
        utilityCharge: mostRecentUtilityStatement?.utilityCharge,
        utilityName: utility?.displayName,
      })
    : getCopy('highlightedTextWithoutBillPay');

  const mainText = needsBillPay
    ? getCopy('text.withoutPaymentMethod')
    : getCopy('text.withoutBillPayOrPaymentMethod');

  // Show dual billing title/copy: bill_capture without bill pay, or cs-acb-retirement + dual_billed
  const showDualText =
    (enrollmentDataSourceName === 'bill_capture' &&
      !billPay?.data?.isAvailable) ||
    (csAcbRetirement && isDualBilled);
  const titleText = showDualText
    ? getCopy('dualBillingTitle')
    : getCopy('title', {
        utilityName: utility?.displayName || getCopy('utilityFallback'),
      });

  return (
    <Card
      margin={{ bottom: '24px' }}
      small={{ margin: { left: '16px', right: '16px' } }}
    >
      <Card.Header>
        <Text tag="h2" margin={{ right: '16px' }} textStyle="heading700">
          {titleText}
        </Text>
      </Card.Header>
      <Card.Content>
        {!!mostRecentUtilityStatement &&
          !!daysUntilBillDue &&
          daysUntilBillDue > 0 && (
            <HighlightedCopyWrapper>
              <Text>{highlightedText}</Text>
            </HighlightedCopyWrapper>
          )}
        <Text opacity="medium">{mainText}</Text>
        {needsBillPay ? (
          <Button
            loading={redirectLoading}
            onClick={
              redirect as (e: React.MouseEvent<HTMLButtonElement>) => void
            }
            fullWidth
            margin={{ top: '16px' }}
          >
            {getCopy('button')}
          </Button>
        ) : (
          <Button
            onClick={() => history.push(paymentSettings)}
            fullWidth
            margin={{ top: '16px' }}
          >
            {getCopy('buttonWithoutBillPay')}
          </Button>
        )}
      </Card.Content>
    </Card>
  );
};
